<script setup lang="ts">

import { formatting } from '@hantera/portal-app';
import { DropdownItem, DropdownMenu } from '@hantera/design-system';
import { computed, ref } from 'vue';
import { COUNTRY_CODES } from '@/constants/country-codes';

const props = defineProps<{
  modelValue: string
  disabled?: boolean
  allowedCountries?: string[]
}>();

const { country } = formatting;

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>();

const button = ref<Element>();
const dropdown = ref<typeof DropdownMenu>();
const error = ref<string | boolean>(false);

defineExpose({
  setError,
});

const allCountries = COUNTRY_CODES.map((code) => ({
  code,
  name: country(code) || '',
})).sort((a, b) => a.name.localeCompare(b.name));

const countries = computed(() => {
  if (props.allowedCountries && props.allowedCountries.length > 0) {
    const allowed = new Set(props.allowedCountries.map(c => c.toUpperCase()))
    return allCountries.filter(c => allowed.has(c.code))
  }
  return allCountries
});

const label = computed(() => {
  return country(props.modelValue) || '-';
});

function toggle() {
  dropdown.value?.open();
}

function select(countryCode: string) {
  emit('update:modelValue', countryCode);
}

function setError(message?: string) {
  if (!message) {
    error.value = true;
  } else {
    error.value = message;
  }
}

</script>

<template>
  <button
    :class="{
      'country-select': true,
      'country-select--disabled': disabled,
      'country-select--error': typeof error === 'string',
    }"
    ref="button"
    @click="toggle"
    :disabled="disabled"
  >
    <div :class="`fi fi-${(modelValue as unknown as string)?.toLowerCase()}`"></div>

    <div class="country-select__label">
      {{ label }}
    </div>

    <div class="country-select__arrow">
      <svg
        class="country-select__icon"
        viewBox="0 0 25 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.174789 3.04115L12.2077 15.3069L24.1485 3.13496L20.6194 0.205244L12.2077 8.77975L3.7039 0.111435L0.174789 3.04115Z"
          fill="currentColor"
        />
      </svg>
    </div>
  </button>
  <div
    class="country-select__error-message"
    :class="{ 'country-select__error-message--hidden': typeof error !== 'string' }"
  >
    {{ error }}
  </div>

  <DropdownMenu
    v-if="button"
    ref="dropdown"
    :target="button"
  >
    <DropdownItem
      v-for="(country) in countries"
      :key="country.code"
      @click="select(country.code as unknown as string)"
    >
      <span :class="`fi fi-${(country.code as unknown as string).toLowerCase()}`"></span> {{ country.name }}
    </DropdownItem>
  </DropdownMenu>
</template>

<style>
.country-select {
  width: 100%;
  display: flex;
  align-items: center;
}

.country-select__label {
  padding: 0 0 0 .7em;
  display: flex;
  align-items: center;
  height: 100%;
  flex-grow: 1;
}

.country-select__arrow {
  padding-left: .7em;
  align-self: stretch;
  display: flex;
  align-items: center;
}

.country-select__icon {
  width: 10px;
}

.country-select__error-message--hidden {
  display: none;
}
</style>
